// Module-level store for `/fetch-coach-client-insights`, read through
// `useClientInsights`. Module scope rather than a context provider because:
//
//   - `ChatDockUserList` reads it *during render* from a deliberately
//     non-memoized IIFE, the same way it reads `getClientName`. A store makes
//     enqueue-on-miss trivial; a provider would need a ref dance.
//   - A provider re-renders every consumer when a poll lands. `RoomsUnreadProvider`
//     goes to some length to avoid exactly that (see its `buildClientRoomIndex`
//     reference guard), and its consumers don't read this data at all.
//
// Same pub/sub shape as `utils/syncQueueStore.js` and `utils/listCache/listCacheStore.js`.

import { fetchCoachClientInsights } from "../../service/SubscriptionWellDhanService";
import { coachInsightsEnabled } from "../../configs/environment";
import { USER_LIST_EVENT_TYPE_ID } from "../../configs/eventTypes";
import { TRACKING_WINDOW_DAYS } from "./insightDerive";

/** Underlying facts move daily; 5 minutes keeps a freshly assigned plan from lagging. */
export const INSIGHTS_TTL_MS = 5 * 60 * 1000;

/** Coalesces the enqueue burst a first render produces into one request. */
const FLUSH_DELAY_MS = 25;

const EMPTY_BY_CLIENT = new Map();

const listeners = new Set();

let byClient = EMPTY_BY_CLIENT;
let status = coachInsightsEnabled ? "idle" : "disabled";
let asOf = null;
let version = 0;
let lastLoadedAt = 0;
let inFlight = null;
let flushTimer = null;

// Latched when the backend answers "I don't serve this" (404/501) or "not for
// you" (403). Those are permanent for the session, so one request settles it —
// which is what lets the flag be switched on before the endpoint ships.
let unavailable = false;

/**
 * Rebuilt only inside the mutation path. `useSyncExternalStore` re-renders in a
 * loop if `getSnapshot` mints a new object per call, so identity stability here
 * is a correctness requirement, not an optimisation.
 */
let snapshot = { byClient, status, asOf, version };

const publish = () => {
  snapshot = { byClient, status, asOf, version };
  listeners.forEach((listener) => listener());
};

/**
 * @param {() => void} listener
 * @returns {() => void} Unsubscribe.
 */
export function subscribeClientInsights(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * @returns {{byClient: Map<string, Object>, status: string, asOf: string|null, version: number}}
 */
export function getClientInsightsSnapshot() {
  return snapshot;
}

/** The payload nests as `{ success, data: { success, data: {...} } }` through ApiService. */
const unwrap = (result) => result?.data?.data ?? result?.data ?? null;

const indexClients = (clients) => {
  const next = new Map();
  (clients || []).forEach((record) => {
    if (record?.client_id == null) return;
    next.set(String(record.client_id), record);
  });
  return next;
};

const load = () => {
  if (inFlight) return inFlight;

  status = "loading";
  publish();

  inFlight = fetchCoachClientInsights(USER_LIST_EVENT_TYPE_ID, {
    tracking_window_days: TRACKING_WINDOW_DAYS,
  })
    .then((result) => {
      if (result?.notImplemented) {
        // Not deployed / not entitled. Stop asking for the rest of the session.
        unavailable = true;
        status = "unavailable";
        return;
      }

      if (!result?.success) {
        // Transient. Keep whatever we already have and retry next TTL window.
        status = "error";
        console.error(
          "clientInsightsStore: failed to load coach client insights",
          result?.message
        );
        return;
      }

      const body = unwrap(result);
      byClient = indexClients(body?.clients);
      asOf = body?.generated_at || null;
      version += 1;
      lastLoadedAt = Date.now();
      status = "ready";
    })
    .catch((err) => {
      status = "error";
      console.error("clientInsightsStore: coach client insights threw", err);
    })
    .finally(() => {
      inFlight = null;
      publish();
    });

  return inFlight;
};

/**
 * Requests the roster's insights if they are missing or stale.
 *
 * Safe to call during render: it only reads module-local state and schedules a
 * timer — the same contract `clientNameBatcher.enqueue` honours, and the reason
 * `ChatDockUserList` can call it from inside its row-building IIFE.
 */
export function ensureClientInsights() {
  if (!coachInsightsEnabled || unavailable || inFlight || flushTimer) return;
  if (status === "ready" && Date.now() - lastLoadedAt < INSIGHTS_TTL_MS) return;

  flushTimer = setTimeout(() => {
    flushTimer = null;
    load();
  }, FLUSH_DELAY_MS);
}

/** Drops all state and un-latches. Call on logout or viewer change. */
export function resetClientInsights() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  byClient = EMPTY_BY_CLIENT;
  status = coachInsightsEnabled ? "idle" : "disabled";
  asOf = null;
  lastLoadedAt = 0;
  unavailable = false;
  inFlight = null;
  version += 1;
  publish();
}
